import { IMouseWheelSpeed } from './mouse-observer';
import { IPosition, ISize } from '../core/domain/dimension';
import { sleep } from './promises';
import { uuid } from 'uuidv4';

export interface IPosition3D extends IPosition {
    z: number;
}

export function clearArray(array: any[]): void {
    array.splice(0, array.length);
}

export function clamp(value: number, minValue: number, maxValue: number): number {
    return value < minValue ? minValue : (value > maxValue ? maxValue : value);
}

export function moveItemInArray(array: any[], oldIndex: number, newIndex: number): void {
    if (newIndex >= array.length) {
        throw new Error('New index out of array bounds');
    }
    array.splice(newIndex, 0, array.splice(oldIndex, 1)[0]);
}

export function decimal(number: number): number {
    return Math.round(number * 100) / 100;
}

export function toFixedDecimal(n: number | string, pad: number = 3): number {
    if (typeof n === 'string') {
        n = parseFloat(n);
    }

    if (isNaN(n)) {
        throw new Error('Could not convert value to a valid number.');
    }

    return parseFloat(n.toFixed(pad));
}

export function createMapFromArray<T>(items: T[], property: keyof T): object {
    const object = {} as object;
    for (const item of items) {
        object[item[property as string] as string] = omit(item, property);
    }
    return object;
}

export function moveItemsInList(list: any[], itemsToMove: any[], toIndex: number): any[] {
    const arrayItemPlaceholder = Symbol();
    const cpy = ([] as any[]).concat(list);
    itemsToMove.forEach((v) => cpy[cpy.indexOf(v)] = arrayItemPlaceholder);
    cpy.splice(list.indexOf(itemsToMove[0]) >= toIndex ? toIndex : (toIndex + itemsToMove.length), 0, ...itemsToMove);
    return cpy.filter((v) => v !== arrayItemPlaceholder);
}

export function deleteItemInArray<T>(array: T[], item: T): void {
    const index = array.indexOf(item);
    if (index === -1) {
        throw new Error('Could not find item in array.');
    }
    array.splice(index, 1);
}

export function deepEqual(x: any, y: any): boolean {
    if ((typeof x === 'object' && x !== null) && (typeof y === 'object' && y !== null)) {
        if (Object.keys(x).length !== Object.keys(y).length) {
            return false;
        }

        for (const prop in x) {
            if (prop.startsWith('__')) {
                continue;
            }
            if (y.hasOwnProperty(prop)) {
                if (!deepEqual(x[prop], y[prop])) {
                    return false;
                }
            }
            else {
                return false;
            }
        }
        return true;
    }
    else if (x !== y) {
        return false;
    }
    return true;
}

export async function awaitAllInMap(promiseMap: any): Promise<Map<string, any>> {
    const result = new Map<string, any>();
    const promises: Promise<any>[] = [];
    const keys: string[] = [];
    for (const i in promiseMap) {
        promises.push(promiseMap[i]);
        keys.push(i);
    }
    const values = await Promise.all(promises);
    let index = 0;
    for (const k of keys) {
        result.set(k, values[index]);
        index++;
    }
    return result;
}

export function has<T>(iter: Iterable<T>, value: T): boolean {
    for (const i of iter) {
        if (i === value) {
            return true;
        }
    }
    return false;
}

export function pick<T, K extends keyof T>(obj: T, ...keys: K[]): Pick<T, K> {
    const newObj = {} as Pick<T, K>;
    for (const k of keys) {
        if (obj[k] !== undefined) {
            newObj[k] = obj[k];
        }
    }
    return newObj;
}

// Todo: Remove Omit type when it is natively defined in TS.
type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

export function omit<T, K extends keyof T>(obj: T, ...keys: K[]): Omit<T, K> {
    const newObj = Object.assign({}, obj);
    for (const key of keys) {
        delete newObj[key];
    }
    for (const key in newObj) {
        if (newObj[key] === undefined) {
            delete newObj[key];
        }
    }
    return newObj;
}


export function autobind(target: any, key: string, descriptor: TypedPropertyDescriptor<any>): TypedPropertyDescriptor<any> | void {
    const fn = descriptor.value;

    if (typeof fn !== 'function') {
        throw new Error(`@autobind decorator can only be applied to methods not: ${typeof fn}`);
    }

    return {
        configurable: true,
        get(this: any): any {
            if (this === target || this.hasOwnProperty(key)) {
                return fn;
            }

            const boundFn = fn.bind(this);
            Object.defineProperty(this, key, {
                configurable: true,
                writable: true,
                enumerable: false,
                value: boundFn
            });
            return boundFn;
        }
    };
}

export function extend<T1, T2>(first: T1, second: T2): T1 & T2 {
    const result: any = {};
    for (const p in first) {
        result[p] = (first as any)[p];
    }
    if (!second) {
        return result;
    }
    for (const p in second) {
        result[p] = (second as any)[p];
    }
    return result;
}

export function clone<T>(x: T): T {
    return extend({} as any, x) as T;
}

const toString = Object.prototype.toString;
export function cloneDeep(value: any): any {
    return cloneDeepHelper(value, new Map());
}

export function cloneMapDeep<M extends Map<any, any> | undefined>(map?: M): M {
    if (!map) {
        return map as M;
    }
    const clonedMap = new Map<any, any>() as M;
    for (const [key, value] of map.entries()) {
        clonedMap!.set(key, cloneDeep(value));
    }
    return clonedMap;
}

export function minutesToMilliseconds(minutes: number): number {
    return minutes * 60000;
}

function cloneDeepHelper(val: any, seen: any): any {
    switch (toString.call(val)) {
        case '[object Array]': {
            if (seen.has(val)) {
                return seen.get(val);
            }
            const copy_1 = val.slice(0);
            seen.set(val, copy_1);
            copy_1.forEach((child, i) => {
                copy_1[i] = cloneDeepHelper(child, seen);
            });
            return copy_1;
        }
        case '[object Object]': {
            if (seen.has(val)) {
                return seen.get(val);
            }
            const copy_2 = Object.create(Object.getPrototypeOf(val));
            seen.set(val, copy_2);
            Object.keys(val).forEach(key => {
                copy_2[key] = cloneDeepHelper(val[key], seen);
            });
            return copy_2;
        }
        default:
            return val;
    }
}

export function checksum(str: string): string {
    let hash = 0, i, chr;
    if (str.length === 0) {
        return hash + '';
    }
    for (i = 0; i < str.length; i++) {
        chr = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + chr;
        hash |= 0; // Convert to 32bit integer
    }
    return hash + '';
}

export function filterObject(obj: any, predicate: (o: any) => {}): any {
    const result = {};
    for (const key in obj) {
        if (obj.hasOwnProperty(key) && predicate(obj[key])) {
            result[key] = obj[key];
        }
    }

    return result;
}

export const charToXmlEntity: { [index: string]: string; } = {
    '&': '&amp;',
    '"': '&quot;',
    '<': '&lt;',
    '>': '&gt;',
};


export const charToHtmlEntity: { [index: string]: string; } = {
    ...charToXmlEntity,
    ' ': '&nbsp;',
};

export function encodeXml(text: string): string {
    if (typeof text !== 'string') {
        return '';
    }

    for (const i in charToXmlEntity) {
        if (i === '\\') {
            text = text.replace(new RegExp('\\\\', 'g'), charToXmlEntity[i]);
        }
        else {
            text = text.replace(new RegExp(i, 'g'), charToXmlEntity[i]);
        }
    }

    return text;
}

export function encodeHtml(text: string): string {
    for (const i in charToHtmlEntity) {
        text = text.replace(new RegExp(i, 'g'), charToHtmlEntity[i]);
    }
    return text;
}

export function decodeXml(text: string): string {
    for (const i in charToXmlEntity) {
        text = text.replace(new RegExp(charToXmlEntity[i], 'g'), i);
    }
    return text;
}

export function base64ToBlob(text: string, contentType: string): Blob {
    const byteCharacters = atob(text);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });
}

export function assert(expression: boolean, message?: string): void {
    if (!expression) {
        throw new Error(message);
    }
}

export function camelCaseToUnderscore(text: string): string {
    return text.replace(/([A-Z])/g, (_, y) => '_' + y.toLowerCase());
}

export function underscoreToCamelCase(text: string): string {
    return text.replace(/(_[a-z])/g, (g) => g[1].toUpperCase());
}

export function underscoreToPascalCase(text: string): string {
    return text.charAt(0).toUpperCase() + text.substring(1).replace(/(_[a-z])/g, (g) => g[1].toUpperCase());
}

export function stringToCamelCase(str: string): string {
    return str.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match: any, index: number) => {
        if (+match === 0) { return ''; } // or if (/\s+/.test(match)) for white spaces
        return index === 0 ? match.toLowerCase() : match.toUpperCase();
    });
}

export interface IReferenceContainer<T> {
    value?: T;
}

export function uuidv4(): string {
    return uuid();
}

function makeCRCTable(): number[] {
    const crcTable: number[] = [];
    let c: number;
    for (let n = 0; n < 256; n++) {
        c = n;
        for (let k = 0; k < 8; k++) {
            c = ((c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1));
        }
        crcTable[n] = c;
    }
    return crcTable;
}

const crcTable = makeCRCTable();

// CRC 32 is a checksum method.
export function crc32(text: string): string {
    let crc = 0 ^ (-1);

    for (let i = 0; i < text.length; i++) {
        crc = (crc >>> 8) ^ crcTable[(crc ^ text.charCodeAt(i)) & 0xFF];
    }

    const numberHash = (crc ^ (-1)) >>> 0;
    const hex = ('00000000000000' + numberHash.toString(16).toUpperCase()).slice(-14);
    return hex;
}

export function getCookie(name: string): string | undefined {
    const value = '; ' + document.cookie;
    const parts = value.split('; ' + name + '=');
    if (parts.length === 2) {
        return parts.pop()!.split(';').shift();
    }
}

export function toDegrees(radians: number): number {
    return radians * 180 / Math.PI;
}

export function toRadians(degrees: number): number {
    return degrees * Math.PI / 180;
}

export function rotatePosition(position: IPosition, origin: IPosition, angle: number): IPosition {
    const cos = Math.cos(-angle);
    const sin = Math.sin(-angle);
    const nx = (cos * (position.x - origin.x)) - (sin * (position.y - origin.y)) + origin.x;
    const ny = (cos * (position.y - origin.y)) + (sin * (position.x - origin.x)) + origin.y;

    return {
        x: nx,
        y: ny,
    };
}

export function rotatePosition3D(pos: IPosition | IPosition3D, angles: IPosition3D, origin: IPosition | IPosition3D = { x: 0, y: 0, z: 0 }): IPosition3D {

    const origin3D = { z: 0, ...origin };
    const pos3D = { z: 0, ...pos };

    const x1 = pos3D.x - origin3D.x,
        y1 = pos3D.y - origin3D.y,
        z1 = pos3D.z - origin3D.z,
        angleX = angles.x / 2,
        angleY = angles.y / 2,
        angleZ = angles.z / 2,

        cr = Math.cos(angleX),
        cp = Math.cos(angleY),
        cy = Math.cos(angleZ),
        sr = Math.sin(angleX),
        sp = Math.sin(angleY),
        sy = Math.sin(angleZ),

        w = cr * cp * cy + -sr * sp * -sy,
        x = sr * cp * cy - (-cr) * sp * -sy,
        y = cr * sp * cy + sr * cp * sy,
        z = cr * cp * sy - (-sr) * sp * -cy,

        m0 = 1 - 2 * (y * y + z * z),
        m1 = 2 * (x * y + z * w),
        m2 = 2 * (x * z - y * w),

        m4 = 2 * (x * y - z * w),
        m5 = 1 - 2 * (x * x + z * z),
        m6 = 2 * (z * y + x * w),

        m8 = 2 * (x * z + y * w),
        m9 = 2 * (y * z - x * w),
        m10 = 1 - 2 * (x * x + y * y);

    return {
        x: x1 * m0 + y1 * m4 + z1 * m8 + origin3D.x,
        y: x1 * m1 + y1 * m5 + z1 * m9 + origin3D.y,
        z: x1 * m2 + y1 * m6 + z1 * m10 + origin3D.z
    };
}

export function getCenter(box: ISize & IPosition): IPosition {
    return {
        x: box.x + box.width / 2,
        y: box.y + box.height / 2,
    };
}

export function getDelta(p1: IPosition, p2: IPosition): IPosition {
    return {
        x: p2.x - p1.x,
        y: p2.y - p1.y,
    };
}



export function polygonIsIntersecting(polygon1: IPosition[], polygon2: IPosition[]): boolean {
    for (const polygon of [polygon1, polygon2]) {
        for (let i1 = 0; i1 < polygon.length; i1++) {
            let min1 = Infinity;
            let max1 = -Infinity;
            let min2 = Infinity;
            let max2 = -Infinity;
            const i2 = (i1 + 1) % polygon.length;
            const p1 = polygon[i1];
            const p2 = polygon[i2];
            const normal = {
                x: p1.y - p2.y,
                y: p2.x - p1.x,
            };

            for (const p of polygon1) {
                const projected = normal.x * p.x + normal.y * p.y;
                if (projected < min1) {
                    min1 = projected;
                }
                if (projected > max1) {
                    max1 = projected;
                }
            }

            for (const p of polygon2) {
                const projected = normal.x * p.x + normal.y * p.y;
                if (projected < min2) {
                    min2 = projected;
                }
                if (projected > max2) {
                    max2 = projected;
                }
            }

            if (max1! < min2! || max2! < min1!) {
                return false;
            }
        }
    }
    return true;
}

export function roundToNearestMultiple(value: number, multiple: number): number {
    return Math.round(value / multiple) * multiple;
}

/**
 * Sort a list of creatives or formats based on their size. The sorting is grouped by width e.g:
 * 160x600, 300x250, 300x300, 468x60
 * Expects the objects to have { width: number, height: number }
 * @param  {[]} arr An array of objects
 * @param  {string} path Path to object key. E.g 'size' or 'creative.size'
 * @returns {[]} Returns the array of objects sorted by width
 */
export function sortByFormatSize<T1>(arr: T1[], path: string = ''): T1[] {
    function sortFn(a: T1, b: T1): number {
        if (typeof a === 'object') {
            const aval = path.slice(0).split('.').reduce(index, a) || a;
            const bval = path.slice(0).split('.').reduce(index, b) || b;
            const aWidth = aval.width;
            const aHeight = aval.height;
            const bWidth = bval.width;
            const bHeight = bval.height;
            if (aWidth === bWidth && aHeight > bHeight) {
                return 1;
            }
            else if (aWidth === bWidth && aHeight < bHeight) {
                return -1;
            }
            else if (aWidth > bWidth) {
                return 1;
            }
            else if (aWidth < bWidth) {
                return -1;
            }
        }
        return 0;
    }

    function index(obj: any, i: any): any {
        return obj[i];
    }

    return arr.sort(sortFn);
}

export function alphaNumericSort(a: string | number, b: string | number): number {
    if (a === b) {
        return 0;
    }
    if (typeof a === typeof b) {
        return a < b ? -1 : 1;
    }
    return typeof a < typeof b ? -1 : 1;
}

export function distinctString(text: string): string {
    return text.replace(/\s/, '')
        .split('')
        // Distinct the array of characters
        .filter((v, i, s) => s.indexOf(v) === i)
        // Sort by alphanumeric
        .sort(alphaNumericSort)
        .join('');
}

/**
 * Retries a promise, waiting between each retry. Useful when making external calls
 * and handling transient failures.
 *
 * @param getPromise function that returns the actual promise to retry
 * @param shouldRetry optional predicate that receives errors thrown during retries.
 *                    If it returns true for an error, the promise is retried. Useful
 *                    when deciding to retry if it is a transient error or something
 *                    more catastrophic
 * @param delays optional array of milliseconds to wait before subsequent retry attempts.
 *               Defaults to 1s, 5s, 10, 20s before throwing the last error
 */
export async function retryPromiseWithDelay<T>(
    getPromise: () => Promise<T>,
    shouldRetry: (...args: any[]) => boolean = (error: any) => true,
    delays: Array<number> = [1000, 5000, 10000, 20000]): Promise<T> {
    let err: any;
    for (const delay of delays) {
        try {
            return await getPromise();
        }
        catch (error) {
            err = error;
            if (shouldRetry(error)) {
                await sleep(delay);
                continue;
            }
            else {
                throw err;
            }
        }
    }
    throw err;
}

export function getAspectRatioFit(srcWidth: number, srcHeight: number, maxWidth: number, maxHeight: number): { width: number, height: number, ratio: number } {
    const ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight);
    return { width: srcWidth * ratio, height: srcHeight * ratio, ratio };
}

export function delay(duration: number): Promise<void> {
    return new Promise<void>((resolve) => {
        setTimeout(resolve, duration);
    });
}

export function generateUniqueName(name: string, listOfItemsWithName: any): string {
    const design = listOfItemsWithName.find(version => version.name === name);
    if (design) {
        if (/\(\d+\)$/.test(name)) {
            const incrementedName = name.replace(/\((\d+?)\)$/g, (_, n) => {
                return '(' + (Number(n) + 1) + ')';
            });
            return generateUniqueName(incrementedName, listOfItemsWithName);
        }
        else {
            return generateUniqueName(`${name} (1)`, listOfItemsWithName);
        }
    }

    return name;
}

export function generateUniqueBrandName(name: string, listOfNames: string[]): string {
    const value = listOfNames.find(current => current === name);
    if (value) {
        if (/\(\d+\)$/.test(name)) {
            const incrementedName = name.replace(/\((.+?)\)$/g, (_fullMatch, n) => {
                return '(' + (Number(n) + 1) + ')';
            });
            return generateUniqueBrandName(incrementedName, listOfNames);
        }
        else {
            return generateUniqueBrandName(`${name} (1)`, listOfNames);
        }
    }

    return name;
}

export function angleToPoints(deg: number): any {
    // In angle is -90 to 270 make it between 0 and 360
    const rad = ((deg + 90) % 360) * Math.PI / 180;
    const segment = Math.floor(rad / Math.PI * 2) + 2;
    const diagonal = (1 / 2 * segment + 1 / 4) * Math.PI;
    const op = Math.cos(Math.abs(diagonal - rad)) * Math.sqrt(2);
    const x = op * Math.cos(rad);
    const y = op * Math.sin(rad);

    return {
        x1: x < 0 ? 1 : 0,
        y1: y < 0 ? 1 : 0,
        x2: x >= 0 ? x : x + 1,
        y2: y >= 0 ? y : y + 1
    };
}

export function distance(p1: IPosition, p2: IPosition): number {
    const x = p1.x - p2.x;
    const y = p1.y - p2.y;
    return Math.sqrt(Math.pow(x, 2) + Math.pow(y, 2));
}

export function sizeDiff(sizeA: ISize, sizeB: ISize): number {
    return ((sizeA.width + sizeA.height) / (sizeB.width + sizeB.height));
}

export function lerp(a: number, b: number, f: number): number {
    return a + f * (b - a);
}

export function drawRoundRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: any, fill?: any, stroke?: any): void {

    if (typeof stroke === 'undefined') {
        stroke = false;
    }
    if (typeof radius === 'undefined') {
        radius = 5;
    }
    if (typeof radius === 'number') {
        radius = { tl: radius, tr: radius, br: radius, bl: radius };
    } else {
        const defaultRadius = { tl: 0, tr: 0, br: 0, bl: 0 };
        for (const side in defaultRadius) {
            radius[side] = radius[side] || defaultRadius[side];
        }
    }
    ctx.beginPath();
    ctx.moveTo(x + radius.tl, y);
    ctx.lineTo(x + width - radius.tr, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius.tr);
    ctx.lineTo(x + width, y + height - radius.br);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius.br, y + height);
    ctx.lineTo(x + radius.bl, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius.bl);
    ctx.lineTo(x, y + radius.tl);
    ctx.quadraticCurveTo(x, y, x + radius.tl, y);
    ctx.closePath();
    if (fill) {
        ctx.fill();
    }
    if (stroke) {
        ctx.stroke();
    }

}

export function sanitizeString(str: string): string {
    if (!str) {
        return '';
    }
    return str
        .replace(/\\/gi, '\\\\')
        .replace(/"/gi, '\"')
        .replace(/\&/g, ';ampersand;')
        .trim();
}

const namespacedCharacters = {
    '&': ';ampersand;'
};

export function resolveNamespacedCharacters(str: string): string {
    if (typeof str !== 'string') {
        return str;
    }

    for (const char in namespacedCharacters) {
        const re = new RegExp(namespacedCharacters[char], 'g');
        str = str.replace(re, char);
    }

    return str;
}

export function stringLiteralArray<T extends string>(a: T[]): T[] {
    return a;
}


export function getMousewheelSpeed(event: WheelEvent): IMouseWheelSpeed {
    if (event.deltaMode === WheelEvent.DOM_DELTA_PIXEL) {
        return {
            x: normalizeSpeed(event.deltaX),
            y: normalizeSpeed(event.deltaY)
        };
    }
    else if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
        return {
            x: normalizeSpeed(event.deltaX) * 35,
            y: normalizeSpeed(event.deltaY) * 35
        };
    }

    return {
        x: event.deltaX,
        y: event.deltaY
    };
}

export function normalizeSpeed(coordinate: number): number {
    return (coordinate % 190 - 0) === -0 ? coordinate / 190 : coordinate / 19;
}

/**
 * Regular btoa does not support non ASCII characters (outside latin1).
 */
export function b64EncodeUnicode(str: string): string {
    // first we use encodeURIComponent to get percent-encoded UTF-8,
    // then we convert the percent encodings into raw bytes which
    // can be fed into btoa.
    return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g,
        function toSolidBytes(match: string, p1: string): string {
            // @ts-ignore
            return String.fromCharCode('0x' + p1);
    }));
}

/**
 * Regular atob does not support non ASCII characters (outside latin1).
 */
export function b64DecodeUnicode(str: string): string {
    // Going backwards: from bytestream, to percent-encoding, to original string.
    return decodeURIComponent(atob(str).split('').map(function(c: string): string {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
}

/*
 * We need this function for handling negative modulus.
 * @link https://web.archive.org/web/20090717035140if_/javascript.about.com/od/problemsolving/a/modulobug.htm
 */
export function mod(n: number, m: number): number {
    return ((n % m) + m) % m;
}
